const RefundPage = () => {
  return (
    <div className="pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <div className="space-y-8">
          {/* Header Section - matches landing page legal style */}
          <header className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-crimson">Legal</p>
            <h1 className="mt-3 font-heading text-4xl font-bold text-foreground sm:text-5xl">
              Refund Policy
            </h1>
            <p className="mt-4 text-muted-foreground">
              Please review our refund policy carefully before making a purchase.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Last Updated: February 20, 2025
            </p>
          </header>

          <div className="prose dark:prose-invert max-w-none space-y-12">
            {/* Introduction */}
            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-card p-6">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  At Cold Case Game Kits, we strive to provide high-quality digital
                  mystery games and excellent customer service. While our products
                  are digital in nature, we understand that issues may arise and
                  we're committed to addressing them fairly.
                </p>
              </div>

              {/* Quick Reference Box - matches landing card style */}
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Quick Reference</h3>
                <div className="grid gap-3 text-sm text-muted-foreground">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Company:</span>
                  <span className="md:col-span-2">DGF Digital Inc.</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Website:</span>
                  <span className="md:col-span-2">ColdCaseGameKits.com</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Email:</span>
                  <span className="md:col-span-2">contact@dgfdigital.com</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Processing Time:</span>
                  <span className="md:col-span-2">Up to 30 days</span>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents - matches landing card style */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-4">Table of Contents</h2>
            <nav className="grid gap-2 text-sm">
              <a href="#general" className="text-crimson hover:underline">1. General Policy</a>
              <a href="#eligibility" className="text-crimson hover:underline">2. Refund Eligibility</a>
              <a href="#non-refundable" className="text-crimson hover:underline">3. Non-Refundable Items</a>
              <a href="#process" className="text-crimson hover:underline">4. Refund Process</a>
              <a href="#timeframes" className="text-crimson hover:underline">5. Timeframes</a>
              <a href="#payment" className="text-crimson hover:underline">6. Payment Processing</a>
              <a href="#exceptions" className="text-crimson hover:underline">7. Exceptions</a>
              <a href="#credits" className="text-crimson hover:underline">8. Store Credits</a>
              <a href="#contact" className="text-crimson hover:underline">9. Contact Information</a>
            </nav>
          </div>

          {/* Main Content Sections */}
          <section id="general" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">1. General Policy</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                Due to the digital nature of our products, all sales are
                generally final. However, we understand that exceptional
                circumstances may arise, and we will review refund requests on a
                case-by-case basis.
              </p>
            </div>
          </section>

          <section id="eligibility" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">2. Refund Eligibility</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p className="mb-4">
                Refunds may be considered in the following situations:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Technical issues preventing access to or completion of the
                  game
                </li>
                <li>Duplicate purchases (verified through our system)</li>
                <li>Unauthorized transactions</li>
                <li>Product significantly different from description</li>
                <li>Billing errors or overcharges</li>
              </ul>
            </div>
          </section>

          <section id="non-refundable" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">3. Non-Refundable Items</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>Digital products that have been downloaded or accessed</li>
                <li>Game credits that have been used or partially used</li>
                <li>
                  Sponsorship purchases after the sponsorship period has begun
                </li>
                <li>Bundle purchases where any component has been accessed</li>
                <li>Special promotional items marked as non-refundable</li>
              </ul>
            </div>
          </section>

          <section id="process" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">4. Refund Process</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p className="mb-4">To request a refund, please provide:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Order number or transaction ID</li>
                <li>Date of purchase</li>
                <li>Reason for refund request</li>
                <li>Description of any technical issues encountered</li>
                <li>Screenshots or error messages (if applicable)</li>
              </ul>
              <p className="mt-4">
                Submit your refund request to contact@dgfdigital.com with the
                subject line "Refund Request - [Order Number]"
              </p>
            </div>
          </section>

          <section id="timeframes" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">5. Timeframes</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Refund requests must be submitted within 14 days of purchase
                </li>
                <li>
                  Technical issues must be reported within 7 days of discovery
                </li>
                <li>Processing time for refund requests is up to 30 days</li>
                <li>
                  Approved refunds will be processed within 5-10 business days
                </li>
              </ul>
            </div>
          </section>

          <section id="payment" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">6. Payment Processing</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>Refunds will be issued to the original payment method</li>
                <li>Processing times vary by payment provider</li>
                <li>International transactions may take additional time</li>
                <li>Currency exchange rates may apply</li>
              </ul>
            </div>
          </section>

          <section id="exceptions" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">7. Exceptions</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p className="mb-4">Special considerations may be made for:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Documented technical issues beyond user control</li>
                <li>Verified billing errors or system malfunctions</li>
                <li>Legal requirements or compliance issues</li>
                <li>Special circumstances evaluated on a case-by-case basis</li>
              </ul>
            </div>
          </section>

          <section id="credits" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">8. Store Credits</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Store credits may be offered in lieu of monetary refunds
                </li>
                <li>Credits are valid for 24 months from date of issue</li>
                <li>Credits cannot be converted to cash</li>
                <li>Credits are non-transferable</li>
              </ul>
            </div>
          </section>

          <section id="contact" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">9. Contact Information</h2>
            </div>
            <div className="rounded-xl border border-crimson/20 bg-crimson/5 p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                Need to Request a Refund?
              </h3>
              <address className="not-italic space-y-2 text-muted-foreground">
                <p className="font-medium text-foreground">DGF Digital Inc.</p>
                <p>9-2020 Lanthier Drive Suite 111</p>
                <p>Orleans, ON K4A 3V4</p>
                <p className="mt-4">
                  <span className="font-medium text-foreground">Email: </span>
                  <a
                    href="mailto:contact@dgfdigital.com"
                    className="text-crimson hover:underline"
                  >
                    contact@dgfdigital.com
                  </a>
                </p>
              </address>
            </div>
          </section>

          {/* Footer - matches landing legal footer */}
          <div className="border-t border-border pt-8 mt-12 text-center text-sm text-muted-foreground space-y-2">
            <p>Effective Date: January 1, 2025</p>
            <p>Last Updated: February 20, 2025</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPage;
